import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Link, Text, Box, Spacing } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import dataFinder from '@/store/dataFinder';
import { useSelector } from 'react-redux';
import usePurchase from 'hooks/store/usePurchase';

/**
 * Converts transport type name.
 * @param {string} transportType - current trip transport type
 * @returns {string} string with readable converted transport name.
 */
function makeCls(transportType) {
  switch (transportType) {
    case 'bus':
      return 'bus';
    case 'flight':
      return 'plane';
    default:
      return '';
  }
}

const propTypes = {
  description: PropTypes.string.isRequired,
  time: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  transportType: PropTypes.string,
  type: PropTypes.string.isRequired,
  index: PropTypes.number,
  brand: PropTypes.string,
  originDetails: PropTypes.object,
  lineDetails: PropTypes.object,
  hasNextDayBadge: PropTypes.bool,
};

const defaultProps = {
  transportType: 'bus',
};

/**
 * Component used as departure and place detail in itinerary.
 * @param {Object} props - Object props
 * @param {string} props.description - Indicates stage of the trip.
 * @param {string} props.time - Indicates trip's stage time.
 * @param {string} props.title - Indicates trip's stage name.
 * @param {string} props.transportType - Indicates trip's transport type .
 * @param {string} props.type - Indicates stage of the trip.
 * @param {string} props.index - Indicates trip index.
 * @param {string} props.brand - Indicates current brand.
 * @param {object} props.originDetails - Indicates trip's origin data.
 * @param {object} props.lineDetails - Indicates line provider data.
 * @param {boolean} props.lineDetails.copyrightProtected - Indicates if data is protected.
 * @param {string} props.lineDetails.siteUrl - Indicates line's site url.
 * @param {string} props.lineDetails.logoUrl - Indicates line's logo url.
 * @param {string} props.lineDetails.providerName - Indicates line's provider name.
 * @param {boolean} props.hasNextDayBadge - Indicates if any segment has a next day badge.
 * @returns {JSX.Element} React component for Trip's timeline departure and place stage .
 */
const PlaceItem = ({
  description,
  time,
  title,
  transportType,
  type,
  index,
  brand,
  originDetails,
  lineDetails: { copyrightProtected = false, siteUrl, logoUrl, providerName, abbr } = {},
  hasNextDayBadge,
}) => {
  const { t } = useTranslation('trips');
  const transportCls = makeCls(transportType);
  const hasCoordinates = originDetails.coordinates?.lat || originDetails.coordinates?.long;
  const features = useWhitelabelFeatures();
  const showTerminal = features.SHOW_TERMINAL_NAME_AT_ITINERARY_ITEM;
  const validatedLogoUrl = logoUrl ?? dataFinder('lines', abbr)?.logoUrl;
  const { bulkTicket: isBulkTicket } = usePurchase();
  const lines = useSelector((state) => state.lines);

  /**
   * Renders the provider logo based on copyright protection and bulk ticket status.
   * @returns {JSX.Element} Provider logo or name component.
   */
  const renderProviderLogo = () => {
    if (copyrightProtected) {
      return (
        <Text size="S" weight="bold">
          {siteUrl}
        </Text>
      );
    }

    if (isBulkTicket) {
      return (
        <Spacing size="XS" alignItems="center">
          {Object.values(lines).map(({ name, logoUrl: url }) => (
            <img
              key={name}
              src={url}
              alt={name}
              className="!h-5 !w-auto !max-w-20 mr-2 aspect-auto"
            />
          ))}
        </Spacing>
      );
    }

    return <img src={validatedLogoUrl} alt={providerName} />;
  };

  return (
    <>
      {brand === 'brasilia' && (
        <Text weight="semibold">
          {t('route')} {index}
        </Text>
      )}
      <div className={`route-detail-row ${type}`}>
        <div className={`rd-left ${hasNextDayBadge ? 'with-next-day' : ''}`}>
          <Text size="S">{time}</Text>
        </div>

        <div className="rd-center">
          <Icon type="Bus" color="primary" size="L" />
        </div>

        <div className={`rd-right ${transportCls}`}>
          <Box
            paddingVertical="S"
            paddingHorizontal="S"
            borderRadius="S"
            border="all"
            borderColor="grayBorder"
            alphaBg="M"
            bgColor="grayBorder"
            hasWidth
          >
            <Spacing size="XS" vertical>
              <Text weight="bold" size="L">
                {title}
              </Text>
              {showTerminal && <Text size="S">{description}</Text>}
              {originDetails.address && <Text size="S">{originDetails.address}</Text>}
            </Spacing>
            {hasCoordinates ? (
              <Link
                newTab
                type="primary"
                size="XS"
                href={`https://www.google.com/maps/search/?api=1&query=${originDetails.coordinates.lat},${originDetails.coordinates.long}`}
                text={t('see_location')}
              />
            ) : null}
            {renderProviderLogo()}
          </Box>
        </div>
      </div>
    </>
  );
};

PlaceItem.propTypes = propTypes;
PlaceItem.defaultProps = defaultProps;

export default PlaceItem;
